import { ArrowLeftIcon } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

import { Skeleton, Text } from '@/design-system/components/ui';

import { ContentContainer } from './components/ContentContainer';

interface Props {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  content?: React.ReactNode;
  children?: React.ReactNode;
  backButton?: { text: string; href: string; onClick?: (e: React.MouseEvent) => void };
  isLoading?: boolean;
}

export const ViewTemplate = ({ title, subtitle, actions, content, backButton, isLoading, children }: Props) => {
  return (
    <main className="tw:flex tw:flex-auto tw:overflow-hidden tw:relative">
      <div className="tw:flex tw:w-full">
        {/* Set "sidebar-template-scrolleable-container" id to match the functionality of InfiniteScroll component */}
        <ContentContainer id="sidebar-template-scrolleable-container">
          <div className="tw:container tw:max-w-[1440px] tw:mx-auto tw:px-6 tw:pb-8">
            <div className="tw:flex tw:flex-col tw:gap-6 tw:animate-view-enter">
              {backButton && (
                <div className="tw:flex tw:flex-col">
                  <Link
                    href={backButton.href}
                    onClick={backButton.onClick}
                    className="tw:inline-flex tw:items-center tw:no-underline tw:text-inherit tw:hover:underline"
                  >
                    <ArrowLeftIcon className="tw:mr-2 tw:w-5 tw:h-5" />
                    <Text variant="body-md" weight="semibold">
                      {backButton.text}
                    </Text>
                  </Link>
                </div>
              )}
              <div className="tw:flex tw:items-start tw:flex-row tw:justify-between tw:gap-6 tw:animate-header-enter">
                <div className="tw:flex tw:flex-col tw:gap-2 tw:animate-title-enter">
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={180} height={36} />
                      <Skeleton variant="text" width={240} height={20} />
                    </>
                  ) : (
                    <>
                      {title && (
                        <Text variant="display-sm" as="h3" className="tw:m-0">
                          {title}
                        </Text>
                      )}
                      {subtitle && (
                        <Text variant="body-md" color="muted">
                          {subtitle}
                        </Text>
                      )}
                    </>
                  )}
                </div>

                <div className="tw:flex tw:items-center tw:flex-row tw:gap-2">{actions}</div>
              </div>
              {content}
              {children}
            </div>
          </div>
        </ContentContainer>
      </div>
    </main>
  );
};
